"use client";

import { useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ArrowRight, ChevronRight, Search, X } from "lucide-react";
import {
  type HelpAudience,
  type SearchableArticle,
  type SearchableHelpSection,
  HELP_AUDIENCE_LABELS,
  searchClientArticles,
  searchClientSections,
  getClientCategories,
  buildSearchTerms,
  normalizeHelpSearch,
  isMeaningfulHelpQuery,
} from "~/lib/help-center-search";
import { cn } from "~/lib/utils";
import { Badge, badgeVariants } from "~/components/ui/badge";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/components/ui/empty";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { PublicPageHeader } from "~/components/public-page-header";
import type { BreadcrumbItem } from "~/components/breadcrumbs";
import {
  SEARCH_FIELD_DROPDOWN_CLASS,
  SEARCH_FIELD_INPUT_CLASS,
  SEARCH_FIELD_WRAPPER_CLASS,
} from "~/components/patterns/search-field-classes";
import { getHelpGroupAnchorId, resolveHelpGroups } from "~/config/help-center-groups";
import { AudienceCatalog } from "./audience-catalog";
import { AudienceSwitcher } from "./audience-switcher";
import { ProspectLanding } from "./prospect-landing";
import { SupportFooterCta } from "./support-footer-cta";

function SearchResultItem({ article }: { article: SearchableArticle }) {
  return (
    <Link
      href={article.slug}
      className="group flex items-start gap-4 rounded-lg px-3 py-4 transition-colors hover:bg-muted/60"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[11px] font-normal">
            {HELP_AUDIENCE_LABELS[article.audience]}
          </Badge>
          <Badge variant="outline" className="text-[11px] font-normal">
            {article.category}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {article.timeToRead} min
          </span>
        </div>
        <h3 className="mt-2 font-semibold tracking-tight group-hover:text-brand">
          {article.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
          {article.description}
        </p>
      </div>
      <ChevronRight className="mt-7 h-4 w-4 shrink-0 text-muted-foreground/30 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
    </Link>
  );
}

function SectionResultItem({ section }: { section: SearchableHelpSection }) {
  return (
    <Link
      href={section.href}
      className="group block rounded-md px-3 py-3 transition-colors hover:bg-muted/60"
    >
      <p className="text-sm font-medium text-foreground/90 group-hover:text-brand">
        {section.heading}
      </p>
      <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
        {section.snippet}
      </p>
    </Link>
  );
}

type SectionResultGroup = {
  slug: string;
  title: string;
  description: string;
  category: string;
  audience: HelpAudience;
  timeToRead: number;
  sections: SearchableHelpSection[];
};

function SectionResultGroupCard({ group }: { group: SectionResultGroup }) {
  return (
    <article className="rounded-lg border bg-background transition-colors hover:border-brand/40">
      <div className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary" className="text-[11px] font-normal">
            {HELP_AUDIENCE_LABELS[group.audience]}
          </Badge>
          <Badge variant="outline" className="text-[11px] font-normal">
            {group.category}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {group.timeToRead} min
          </span>
        </div>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Link
              href={group.slug}
              className="font-semibold tracking-tight hover:text-brand"
            >
              {group.title}
            </Link>
            <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
              {group.description}
            </p>
          </div>
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/30" />
        </div>
      </div>
      <div className="border-t p-1">
        {group.sections.slice(0, 3).map((section) => (
          <SectionResultItem key={section.id} section={section} />
        ))}
      </div>
    </article>
  );
}

const emptySearchSuggestions = [
  {
    label: "Zakazivanje",
    href: "/podrska/klijenti/zakazivanje-i-termini",
  },
  {
    label: "Plaćanje",
    href: "/podrska/klijenti/cene-i-placanje",
  },
  {
    label: "Video sesija",
    href: "/podrska/klijenti/tehnicki-problemi-na-sesiji",
  },
  {
    label: "Privatnost",
    href: "/podrska/klijenti/privatnost-i-bezbednost",
  },
];

const quickSearches = [
  "zakazivanje",
  "plaćanje",
  "video poziv",
  "otkazivanje",
  "privatnost",
];

function formatTopicCount(count: number) {
  const lastTwoDigits = count % 100;
  const lastDigit = count % 10;

  if (count === 1) return "1 tema";
  if (
    lastDigit >= 2 &&
    lastDigit <= 4 &&
    (lastTwoDigits < 12 || lastTwoDigits > 14)
  ) {
    return `${count} teme`;
  }

  return `${count} tema`;
}

function normalizeForHighlight(text: string) {
  let normalized = "";
  const indexMap: number[] = [];

  Array.from(text).forEach((character, index) => {
    const normalizedCharacter = normalizeHelpSearch(character);
    normalized += normalizedCharacter;
    for (let i = 0; i < normalizedCharacter.length; i += 1) {
      indexMap.push(index);
    }
  });

  return { normalized, indexMap };
}

function HighlightQuery({
  text,
  query,
}: {
  text: string;
  query: string;
}) {
  const terms = buildSearchTerms(query);
  if (terms.length === 0) return <>{text}</>;

  const { normalized, indexMap } = normalizeForHighlight(text);

  const ranges: Array<{ start: number; end: number }> = [];

  for (const term of terms) {
    let start = normalized.indexOf(term);
    while (start !== -1) {
      const normalizedEnd = start + term.length - 1;
      const originalStart = indexMap[start];
      const originalEnd = indexMap[normalizedEnd];

      if (originalStart !== undefined && originalEnd !== undefined) {
        ranges.push({ start: originalStart, end: originalEnd + 1 });
      }

      start = normalized.indexOf(term, start + term.length);
    }
  }

  if (ranges.length === 0) return <>{text}</>;

  const mergedRanges = ranges
    .sort((a, b) => a.start - b.start || b.end - a.end)
    .reduce<Array<{ start: number; end: number }>>((merged, range) => {
      const previous = merged.at(-1);
      if (!previous || range.start > previous.end) {
        merged.push({ ...range });
        return merged;
      }

      previous.end = Math.max(previous.end, range.end);
      return merged;
    }, []);

  const parts: ReactNode[] = [];
  let cursor = 0;

  for (const range of mergedRanges) {
    if (range.start > cursor) {
      parts.push(text.slice(cursor, range.start));
    }

    parts.push(
      <mark
        key={`${range.start}-${range.end}`}
        className="rounded-sm bg-brand/10 px-0.5 text-brand"
      >
        {text.slice(range.start, range.end)}
      </mark>,
    );
    cursor = range.end;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return <>{parts}</>;
}

export function HelpCenterClient({
  articles,
  sections,
  basePath,
  title,
  description,
  breadcrumbItems,
  density = "compact",
  initialQuery,
  initialCategory,
  forcedAudience,
}: {
  articles: SearchableArticle[];
  sections: SearchableHelpSection[];
  basePath: string;
  title: string;
  description: string;
  breadcrumbItems: BreadcrumbItem[];
  density?: "compact" | "hero";
  initialQuery?: string;
  initialCategory?: string;
  forcedAudience?: HelpAudience;
}) {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(initialQuery ?? "");
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery ?? "");
  const [category, setCategory] = useState<string | undefined>(initialCategory);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  useEffect(() => {
    setQuery(initialQuery ?? "");
    setSubmittedQuery(initialQuery ?? "");
    setCategory(initialCategory);
  }, [initialCategory, initialQuery]);

  useEffect(() => {
    const params = new URLSearchParams();
    const q = submittedQuery.trim();
    if (isMeaningfulHelpQuery(q)) params.set("q", q);
    if (category) params.set("category", category);
    const qs = params.toString();
    router.replace(qs ? `${basePath}?${qs}` : basePath, { scroll: false });
  }, [submittedQuery, category, basePath, router]);

  const trimmedSubmittedQuery = submittedQuery.trim();
  const hasDraftQuery = isMeaningfulHelpQuery(query);
  const hasSubmittedQuery = isMeaningfulHelpQuery(trimmedSubmittedQuery);
  const hasFilters = Boolean(hasSubmittedQuery || category);
  const isLanding = !hasFilters && !forcedAudience;

  const articleResults = useMemo(
    () =>
      searchClientArticles(articles, submittedQuery, forcedAudience, category),
    [articles, submittedQuery, forcedAudience, category],
  );

  const sectionResults = useMemo(
    () =>
      searchClientSections(sections, submittedQuery, forcedAudience, category),
    [sections, submittedQuery, forcedAudience, category],
  );

  const suggestionResults = useMemo(
    () => searchClientSections(sections, query, forcedAudience, category),
    [sections, query, forcedAudience, category],
  );

  const sectionResultGroups = useMemo(() => {
    if (!hasSubmittedQuery) return [];

    const groups = new Map<string, SectionResultGroup>();

    for (const section of sectionResults) {
      const group = groups.get(section.slug);
      if (group) {
        group.sections.push(section);
        continue;
      }

      groups.set(section.slug, {
        slug: section.slug,
        title: section.title,
        description: section.description,
        category: section.category,
        audience: section.audience,
        timeToRead: section.timeToRead,
        sections: [section],
      });
    }

    return Array.from(groups.values());
  }, [hasSubmittedQuery, sectionResults]);

  const resultsCount = hasSubmittedQuery
    ? sectionResultGroups.length
    : articleResults.length;

  const searchSuggestions = useMemo(
    () => suggestionResults.slice(0, 3),
    [suggestionResults],
  );

  const suggestionTopicCount = useMemo(
    () => new Set(suggestionResults.map((section) => section.slug)).size,
    [suggestionResults],
  );

  const showSearchSuggestions =
    isSearchFocused && hasDraftQuery && searchSuggestions.length > 0;

  const categories = useMemo(
    () => getClientCategories(articles, forcedAudience),
    [articles, forcedAudience],
  );

  const clientArticles = useMemo(
    () =>
      forcedAudience
        ? articles.filter((a) => a.audience === forcedAudience)
        : articles,
    [articles, forcedAudience],
  );

  const audienceGroups = useMemo(
    () => (forcedAudience ? resolveHelpGroups(forcedAudience, clientArticles) : []),
    [forcedAudience, clientArticles],
  );

  function clearSearch() {
    setQuery("");
    setSubmittedQuery("");
    setCategory(undefined);
    setIsSearchFocused(false);
    inputRef.current?.focus();
  }

  function applyQuickSearch(value: string) {
    setQuery(value);
    setSubmittedQuery(value);
    setCategory(undefined);
    setIsSearchFocused(false);
    inputRef.current?.focus();
    window.setTimeout(() => {
      resultsRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 0);
  }

  function showAllResults() {
    const q = query.trim();
    if (!isMeaningfulHelpQuery(q)) return;
    setSubmittedQuery(q);
    setIsSearchFocused(false);
    window.setTimeout(() => {
      resultsRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 0);
  }

  function openSection(href: string) {
    setIsSearchFocused(false);
    router.push(href);
  }


  return (
    <>
      <PublicPageHeader
        breadcrumbItems={breadcrumbItems}
        density={density}
        title={title}
        subtitle={description}
      />

      {/*
        Matches `TherapyDirectoryToolbar`'s wrapper exactly: a separate
        full-width band directly below the header, not header `children`.
        The search field also borrows the directory's `TopicSwitcher` class
        strings (see `~/components/patterns/search-field-classes`) so the
        two search fields read as one system. The group anchor nav for
        audience pages lives here too, below the pills, rather than in the
        header's `afterTitle`, since everything search-related now lives in
        one band.
      */}
      <section className="border-y border-border bg-secondary">
        <div className="max-w-8xl container mx-auto flex flex-col gap-4 px-4 py-5 md:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <Command
              shouldFilter={false}
              className="relative min-w-0 max-w-2xl flex-1 overflow-visible bg-transparent text-foreground"
            >
            <CommandInput
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => {
                window.setTimeout(() => setIsSearchFocused(false), 100);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && hasDraftQuery) {
                  event.preventDefault();
                  showAllResults();
                }
                if (event.key === "Escape") {
                  setIsSearchFocused(false);
                }
              }}
              placeholder='Pretraži, npr. "otkazivanje" ili "video poziv"'
              wrapperClassName={SEARCH_FIELD_WRAPPER_CLASS}
              className={cn(SEARCH_FIELD_INPUT_CLASS, "pr-10")}
              aria-expanded={showSearchSuggestions}
              aria-controls="help-search-suggestions"
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-3 z-10 text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Očisti pretragu"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {showSearchSuggestions && (
              <CommandList
                id="help-search-suggestions"
                className={SEARCH_FIELD_DROPDOWN_CLASS}
                onMouseDown={(event) => event.preventDefault()}
              >
                <CommandGroup heading="Najbliži odgovori">
                  {searchSuggestions.map((section) => (
                    <CommandItem
                      key={section.id}
                      value={`${section.title} ${section.heading}`}
                      onSelect={() => openSection(section.href)}
                      className="flex cursor-pointer flex-col items-start gap-1 px-3 py-3"
                    >
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className="text-[11px] font-normal"
                        >
                          {section.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {section.timeToRead} min
                        </span>
                      </div>
                      <span className="text-sm font-semibold tracking-tight">
                        <HighlightQuery text={section.title} query={query} />
                      </span>
                      <span className="line-clamp-1 text-xs font-medium text-muted-foreground">
                        <HighlightQuery text={section.heading} query={query} />
                      </span>
                      <span className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                        <HighlightQuery text={section.snippet} query={query} />
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandItem
                  value="Prikaži sve rezultate"
                  onSelect={showAllResults}
                  className="flex min-h-11 cursor-pointer items-center justify-between border-t px-3 py-0 text-sm font-medium text-brand"
                >
                  <span>
                    {`Prikaži sve rezultate (${formatTopicCount(
                      suggestionTopicCount,
                    )})`}
                  </span>
                  <span className="flex items-center gap-2">
                    <kbd className="rounded-sm border bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                      Enter
                    </kbd>
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </CommandItem>
              </CommandList>
              )}
            </Command>
            <AudienceSwitcher audience={forcedAudience} />
          </div>
          <div className="flex max-w-2xl flex-wrap gap-2">
            {quickSearches.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => applyQuickSearch(item)}
                className="rounded-full border bg-background px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-brand/40 hover:text-foreground"
              >
                {item}
              </button>
            ))}
          </div>
          {forcedAudience && audienceGroups.length > 0 && (
            <nav
              aria-label="Teme"
              className="flex flex-wrap gap-x-5 gap-y-2 border-t pt-4 text-sm"
            >
              {audienceGroups.map((group) => {
                const anchorId = getHelpGroupAnchorId(group.definition);
                return (
                  <a
                    key={anchorId}
                    href={`#${anchorId}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {group.definition.title}
                  </a>
                );
              })}
            </nav>
          )}
        </div>
      </section>

      {isLanding ? (
        <ProspectLanding articles={clientArticles} />
      ) : !hasFilters && forcedAudience ? (
        <AudienceCatalog groups={audienceGroups} />
      ) : (
        <div className="bg-background">
          <div className="max-w-8xl container mx-auto px-4 py-8 md:px-8">
            <div ref={resultsRef} className="max-w-4xl">
              <div className="flex items-start justify-between gap-4 pb-3">
                <div>
                  <p className="text-sm font-medium">
                    {hasSubmittedQuery
                      ? formatTopicCount(resultsCount)
                      : resultsCount}
                    {!hasSubmittedQuery && " "}
                    {!hasSubmittedQuery &&
                      (resultsCount === 1 ? "rezultat" : "rezultata")}
                    {trimmedSubmittedQuery ? ` za "${trimmedSubmittedQuery}"` : ""}
                  </p>
                  {(forcedAudience || category) && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {forcedAudience
                        ? HELP_AUDIENCE_LABELS[forcedAudience]
                        : "Svi članci"}
                      {category ? ` · ${category}` : ""}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Očisti
                </button>
              </div>

              {categories.length > 1 && resultsCount > 0 && (
                <div className="flex flex-wrap gap-1.5 pb-6">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() =>
                        setCategory(cat === category ? undefined : cat)
                      }
                      className={cn(
                        badgeVariants({
                          variant: cat === category ? "default" : "outline",
                        }),
                        "cursor-pointer font-normal",
                        cat !== category && "hover:bg-secondary",
                      )}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}

              <div className="pb-12">
                {resultsCount === 0 ? (
                  <Empty className="rounded-lg border bg-muted/20">
                    <EmptyMedia variant="icon">
                      <Search className="h-5 w-5" />
                    </EmptyMedia>
                    <EmptyHeader>
                      <EmptyTitle>Nema rezultata za ovu pretragu</EmptyTitle>
                      <EmptyDescription>
                        Probaj kraći izraz, na primer “termin”, “plaćanje”,
                        “kamera” ili “podaci”.
                      </EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent className="max-w-xl">
                      <div className="flex flex-wrap justify-center gap-2">
                        {emptySearchSuggestions.map((suggestion) => (
                          <Link
                            key={suggestion.href}
                            href={suggestion.href}
                            className="inline-flex h-9 items-center rounded-md border bg-background px-3 text-sm font-medium transition-colors hover:bg-muted"
                          >
                            {suggestion.label}
                          </Link>
                        ))}
                      </div>
                      <SupportFooterCta />
                    </EmptyContent>
                  </Empty>
                ) : (
                  <div className="mt-2 space-y-1">
                    {hasSubmittedQuery
                      ? sectionResultGroups.map((group) => (
                          <SectionResultGroupCard
                            key={group.slug}
                            group={group}
                          />
                        ))
                      : articleResults.map((article) => (
                          <SearchResultItem
                            key={article.slug}
                            article={article}
                          />
                        ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
